import { useState, useEffect, useContext } from 'react';
import { ClientContext, useQuery } from 'react-fetching-library';
import qs from 'qs';

import useMetadataUpdate from 'hooks/useMetadataUpdate';
import useDidUpdate from 'hooks/useDidUpdate';
import { ssItems } from 'config/sessionStorage';
import useExportCsv from 'hooks/useExportCsv';
import { getEndpoint, getTokenEndpoint } from 'helpers/endpoint';
import { QUERY_DELAY } from 'components/Shared/constants';
import { useDispatch, useSelector } from 'react-redux';
import { checkIsMaintenanceMode } from '../helpers/downForMaintenance';
import { updateIsMaintenanceMode } from '../redux_store/reducer/reducers/userReducer/userReducer';
import { TEXT_FILTERS } from '../components/Shared/Table/helpers';
import { useParams } from 'react-router-dom';
import { DEFAULT_TABLES_SORTING } from '../redux_store/reducer/reducers/tableReducer/tableReducer';

export const ITEMS_PER_PAGE = parseInt(window.innerHeight / 60) + 20;

export const updateObjectValues = (obj) => {
  const updateFn = (optionsAPI) => {
    const res = [];
    Object.keys(optionsAPI).forEach((key) => {
      if (Array.isArray(optionsAPI[key])) {
        res.push({
          label: key,
          value: key,
          child: optionsAPI[key].map((item) => item.key),
        });
        optionsAPI[key].forEach(({ key, color }) => {
          res.push({
            label: key,
            value: key,
            isChild: true,
            color: color,
          });
        });
      } else {
        res.push({
          label: key,
          value: key,
          color: optionsAPI[key],
        });
      }
    });
    return res;
  };

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, updateFn(value)]),
  );
};

let filterTimer;

String.prototype.hashCode = function () {
  let hash = 0,
    i, chr;
  if (this.length === 0) return hash;
  for (i = 0; i < this.length; i++) {
    chr = this.charCodeAt(i);
    hash = ((hash << 5) - hash) + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

const checkIsFiltersExpired = () => {
  const currentData = JSON.parse(sessionStorage.getItem(ssItems.FILTERS)) || {};
  const today = new Date();
  if (currentData.date !== today.toDateString()) {
    sessionStorage.removeItem(ssItems.FILTERS);
    const currentData = { date: today.toDateString() };
    sessionStorage.setItem(ssItems.FILTERS, JSON.stringify(currentData));
  }
}

const getFromLocalStorage = (tableKey, currentAccount) => {
  checkIsFiltersExpired()
  const currentData = JSON.parse(sessionStorage.getItem(ssItems.FILTERS));
  const currentAccountUuid = (currentAccount ? currentAccount.uuid : 'user').hashCode();
  return currentData && currentData[currentAccountUuid] ? currentData[currentAccountUuid][tableKey] || {} : {};
};

const setToLocalStorage = (tableKey, currentAccount, sort, filter) => {
  checkIsFiltersExpired()
  const currentData = JSON.parse(sessionStorage.getItem(ssItems.FILTERS)) || {};
  const currentAccountUuid = (currentAccount ? currentAccount.uuid : 'user').hashCode();
  currentData[currentAccountUuid] = {
    ...(currentData[currentAccountUuid] || {}),
    [tableKey]: {
      sort,
      filter,
    },
  };
  sessionStorage.setItem(ssItems.FILTERS, JSON.stringify(currentData));
};

const useFetchList = ({
  baseEndpoint,
  baseParams,
  exportEndpoint,
  baseFilter,
  customMethod,
  patches,
  tableKey,
  preferBaseFilter,
  additionalLoads,
  ignorePrefilters,
  canLoad = true,
}) => {
  /*
    items: Array,
    totalCount: Number,
    continuationToken: String
  */
  const client = useContext(ClientContext);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [list, setList] = useState({});
  const [_canLoad, setCanLoad] = useState(canLoad);
  const [sort, setSort] = useState(null);
  const [filter, setFilter] = useState(null);
  const [additionalLoadsResults, setAdditionalLoadsResults] = useState(null);
  const { patientUuid: paramsPatientUuid } = useParams();
  const isPatientPage = Boolean(paramsPatientUuid) || ignorePrefilters;
  const paramsEndpoint = `${baseEndpoint}${qs.stringify(baseParams, {
    addQueryPrefix: true,
  })}`;

  async function fetchByKey(urlMap) {
    const entries = Object.entries(urlMap); // [['key1', 'url1'], ['key2', 'url2'], ...]
    const results = await Promise.all(
      entries.map(async ([key, request]) => {
        const { payload, error } = await client.query({
          method: 'GET',
          endpoint: request,
        });
        return [key, error ? null : payload];
      }),
    );
    const updated = updateObjectValues(Object.fromEntries(results));
    return updated; // { key1: data1, key2: data2, ... }
  }

  const clearEmptyFilter = (newFilter) => {
    let filter = [...newFilter];
    return filter.filter(({ value }) => {
      if (Array.isArray(value)) {
        return value.length > 0 && value.join('').replace(' ', '').length > 0;
      } else {
        return value;
      }
    });
  };
  /*
    endpoint: String
    fetchAll: Boolean - load all the items (check all checkbox)
    pagesToLoad: Number - number of pages to load immediately
      (remove item, scroll down using scrollbar)
  */
  const [params, setParams] = useState({
    endpoint: '',
    fetchAll: false,
    pagesToLoad: 1,
  });
  const { endpoint, fetchAll, pagesToLoad } = params;

  const {
    metadata: { currentAccount },
  } = useSelector((state) => state.user);

  const getParams = (patches) => ({
    method: customMethod ?? 'GET',
    endpoint,
    body: patches,
  });

  const { query, loading } = useMetadataUpdate(useQuery, [
    getParams(patches),
    false,
  ]);

  const { fetchExportItems, exportItems, exportItemsLoadingStatus } = useExportCsv({
    baseEndpoint: exportEndpoint,
    baseParams: {
      ...baseParams,
      fields: undefined,
    },
    sort,
    filter,
  });

  // load filter/sort params from session storage
  useEffect(() => {
    let {
      sort,
      filter = baseFilter,
    } = isPatientPage ? {} : getFromLocalStorage(tableKey, currentAccount);

    let _filter = preferBaseFilter ? (baseFilter || filter || []) : filter || [];
    if ((!sort || !sort.length || !sort[0].id) && Object.keys(DEFAULT_TABLES_SORTING).includes(tableKey)) {
      sort = [DEFAULT_TABLES_SORTING[tableKey]];
    }
    setSort(sort);
    setFilter(_filter);
    if (canLoad) { // waiting fields
      setParams({
        ...params,
        endpoint: getEndpoint(baseEndpoint, baseParams, sort, _filter),
      });
    }
  }, []);

  useEffect(() => {
    if (!_canLoad && canLoad) {
      setCanLoad(true)
      setParams({
        ...params,
        endpoint: getEndpoint(baseEndpoint, baseParams, sort, filter),
      });
    }
  }, [canLoad]);

  useEffect(() => {
    if (list.items) {
      setIsLoading(false);
    }
  }, [list.items]);

  // save filter/sort params to session storage
  useEffect(() => {
    if ((sort !== null || filter !== null) && !isPatientPage) {
      setToLocalStorage(tableKey, currentAccount, sort, filter);
    }
  }, [sort, filter]);

  /*
    Clear filters and fetch new list on current account changing
    or on base endpoint changing.
    Need to trigger an endpoint changing even if it's the same.
    Case: accounts list
  */
  useDidUpdate(() => {
    const {
      sort,
      filter = baseFilter,
    } = isPatientPage ? {} : getFromLocalStorage(tableKey, currentAccount);
    let newFilter = preferBaseFilter ? (baseFilter || filter || []) : filter || [];
    const newSort = sort || [];
    let newEndpoint = getEndpoint(baseEndpoint, baseParams, newSort, newFilter);
    if (endpoint === newEndpoint) {
      newEndpoint += ' ';
    }

    setList({});
    setSort(newSort);
    setFilter(newFilter);
    setParams({
      endpoint: newEndpoint,
      fetchAll: false,
      pagesToLoad: 1,
    });
  }, [currentAccount.uuid, paramsEndpoint]);

  const fetchNewData = () => {
    const setNewEndpoint = (token) => {
      setParams({
        ...params,
        endpoint: getTokenEndpoint(baseEndpoint, token),
      });
    };

    const fetchData = async () => {
      if (endpoint) {
        if (!additionalLoadsResults) {
          const res = await fetchByKey(additionalLoads || {});
          setAdditionalLoadsResults(res);
        }
        setIsLoading(true);
        setTimeout(async () => {
          const { error, payload } = await query(patches);
          if (!error) {
            const newList = {
              totalCount: list.totalCount,
              totalAccountsCount: list.totalAccountsCount,
              completionRate: list.completionRate,
              noShowRate: list.noShowRate,
              performanceRate: list.performanceRate,
              ...payload,
              aggregations: params.pagesToLoad === 1
                ? payload?.aggregations || {}
                : payload?.aggregations || list.aggregations,
            };
            if (endpoint.includes('continuation-token')) {
              newList.items = [].concat(list.items, payload.items);
            }

            if (payload.result) {
              newList.items = payload.result;
            }

            setList(newList);

            // need to load more than 1 page at once
            if (Math.ceil(newList.items.length / ITEMS_PER_PAGE) < pagesToLoad) {
              if (newList.continuationToken && list.totalCount < newList.items.length) {
                setNewEndpoint(newList.continuationToken);
              } else {
                setParams({
                  ...params,
                  pagesToLoad: 1,
                });
              }
              // need to load all the pages
            } else if (fetchAll) {
              if (newList.items.length !== newList.totalCount) {
                setNewEndpoint(newList.continuationToken);
              } else {
                setParams({
                  ...params,
                  fetchAll: false,
                });
              }
            }
          } else {
            const response = await checkIsMaintenanceMode();
            if (response.status === 200) {
              dispatch(updateIsMaintenanceMode(true));
            }
          }
        }, 100);
      }
    };

    fetchData();
  };

  // fetch data on endpoint changing
  useEffect(() => {
    fetchNewData();
  }, [endpoint, query]);

  const onSort = (newSort) => {
    const _sort = newSort(sort);
    setParams({
      ...params,
      endpoint: getEndpoint(baseEndpoint, baseParams, _sort, filter),
    });
    setSort(_sort);
  };

  const onFilter = async (_filter, hasDelay) => {
    // hasDelay - only for text field
    const newFilter = clearEmptyFilter(_filter(filter));
    let _hasDelay = hasDelay || false;
    const prewFilterAsObj = {};
    const newFilterAsObj = {};
    filter.forEach(({ id, value }) => prewFilterAsObj[id] = value);
    newFilter.forEach(({ id, value }) => newFilterAsObj[id] = value);

    Object.keys(newFilterAsObj).forEach(newFilterKey => {
      if (newFilterAsObj[newFilterKey] !== prewFilterAsObj[newFilterKey]) {
        if (TEXT_FILTERS.includes(newFilterKey)) {
          _hasDelay = true;
        }
      }
    });

    if (JSON.stringify(filter) !== JSON.stringify(newFilter)) {
      setFilter(newFilter);

      await clearTimeout(filterTimer);

      if (_hasDelay) {
        filterTimer = setTimeout(() => {
          setParams({
            ...params,
            endpoint: getEndpoint(baseEndpoint, baseParams, sort, newFilter),
          });
        }, QUERY_DELAY);
      } else {
        setParams({
          ...params,
          endpoint: getEndpoint(baseEndpoint, baseParams, sort, newFilter),
        });
      }
    }
  };

  const onMultiColumnFilter = (newFilters, hasDelay) => {
    const newFilter = clearEmptyFilter(newFilters);

    clearTimeout(filterTimer);
    filterTimer = setTimeout(() => {
      setParams({
        ...params,
        endpoint: getEndpoint(baseEndpoint, baseParams, sort, newFilter),
      });
      setFilter(newFilter);
    }, hasDelay ? QUERY_DELAY : 0);
  };

  const clearFilter = () => {
    const newFilter = [];
    setParams({
      ...params,
      endpoint: getEndpoint(baseEndpoint, baseParams, sort, newFilter),
    });
    setFilter(newFilter);
  };

  const onLoadMoreRows = (stopIndex) => {
    const token = list.continuationToken;
    if (list.items && stopIndex > list.items.length - 1 && token && !loading) {
      setParams({
        ...params,
        endpoint: getTokenEndpoint(baseEndpoint, token),
        pagesToLoad: Math.ceil((stopIndex + 1) / ITEMS_PER_PAGE),
      });
    }
  };

  const fetchAllItems = () => {
    if (fetchAll) {
      return;
    }
    const token = list.continuationToken;
    if (token) {
      setParams({
        ...params,
        endpoint: getTokenEndpoint(baseEndpoint, token),
        fetchAll: true,
      });
    }
  };

  const reloadData = (props) => {
    let DELAY = QUERY_DELAY;
    // eslint-disable-next-line no-prototype-builtins
    if (props && props.hasOwnProperty('delay')) {
      DELAY = props.delay;
    }
    const pages = Math.ceil(list?.items?.length / ITEMS_PER_PAGE);
    // need to trigger endpoint changing
    setTimeout(() => {
      setParams({
        ...params,
        pagesToLoad: pages,
      });
      fetchNewData();
    }, DELAY);
  };

  return {
    items: list.items,
    totalAccountsCount: list.totalAccountsCount,
    aggregations: list.aggregations,
    completionRate: list.completionRate,
    noShowRate: list.noShowRate,
    performanceRate: list.performanceRate,
    totalCount: list.totalCount,
    onLoadMoreRows,
    fetchAllItems,
    sort,
    onSort,
    filter,
    clearFilter,
    onFilter: onFilter,
    onMultiColumnFilter: onMultiColumnFilter,
    reloadData,
    exportItems,
    fetchExportItems,
    exportItemsLoadingStatus,
    loading: isLoading,
    additionalLoadsResults: additionalLoadsResults,
  };
};

export default useFetchList;
